import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Button from '../../../react/components/Button';
import Loader from '../../../../web/images/svg/loader.svg';

import {
  useSelectSeatsMutation,
} from '../redux/tessituraApiSlice';

import { useAppDispatch, useAppSelector } from '../hooks';
import { genericErrorMessage, getFixedCachedKey } from '../utils';
import { updatePromoCode, updateSuccessMessages } from '../redux/sessionSlice';

const ResetPromoCode = ({ handleCloseModal, parentRef }: ResetPromoCodeProps) => {
  const cacheKey = 'select-seats';

  const dispatch = useAppDispatch();

  const [
    selectSeats,
    {
      data,
      error,
      isLoading,
      isSuccess,
      reset,
    },
  ] = useSelectSeatsMutation({ fixedCacheKey: getFixedCachedKey(cacheKey) });

  const {
    currentErrorMessage, promoCode, seats, specialRequests,
  } = useAppSelector((state) => state.sessionSlice);

  const [closeCurrentModal, setCloseCurrentModal] = useState(false);

  const getErrorMessage = () => (
    error && currentErrorMessage
      ? currentErrorMessage || genericErrorMessage
      : genericErrorMessage
  );

  useEffect(() => {
    if (isSuccess && data?.message && promoCode !== '') {
      dispatch(updatePromoCode(''));
      dispatch(updateSuccessMessages(data?.message));
      setCloseCurrentModal(true);
    }
  }, [isSuccess, dispatch, data?.message, promoCode, data]);

  useEffect(() => {
    if (closeCurrentModal) {
      reset();
      handleCloseModal();
      setCloseCurrentModal(true);
    }
  }, [closeCurrentModal, handleCloseModal, reset]);

  useEffect(() => {
    if ((error) && 'current' in parentRef) {
      const modal = parentRef.current;
      if (modal) modal.scrollBy(0, modal.scrollHeight);
    }
  }, [error, parentRef]);

  const handleRemovePromoCode = async () => {
    try {
      await selectSeats({
        promoCode: 'reset',
        seats,
        specialRequests,
      });
    } catch ({ errorMessage }) {
      throw new Error(`${errorMessage}`);
    }
  };

  return (
    <div className="c-cyop__modal-buttons">
      {error && <div className="c-cyop__modal-error">{getErrorMessage()}</div>}
      <Button
        cyopInverse
        cyopModal
        onClick={handleCloseModal}
        onKeyDown={handleCloseModal}
      >
        Go Back
      </Button>
      <Button
        cyopModal
        disabled={isLoading}
        onClick={handleRemovePromoCode}
        onKeyDown={handleRemovePromoCode}
        cyopModalWithLoader={isLoading}
      >
        I Understand, Confirm
        {(isLoading) && (
        <div className="c-loader c-loader--button c-loader--dark c-loader--small"><Loader /></div>
        )}
      </Button>
    </div>
  );
};

const resetPromoCodePropTypes = {
  handleCloseModal: PropTypes.func.isRequired,
  parentRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]).isRequired,
};

ResetPromoCode.propTypes = resetPromoCodePropTypes;

type ResetPromoCodeProps = PropTypes.InferProps<typeof resetPromoCodePropTypes>;

export default ResetPromoCode;
